import { ButtonV2, Modal, Spinner } from '@cimplico/components';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { ReactComponent as CertifyImage } from '../../../assets/certify.svg';
import { toastNotification } from '../../../components/toast/Toast';
import { useUserContext } from '../../../providers/UserProvider';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { useCertifyWorkpaper } from '../../../queries/use-certify-workpaper';
import { useRevokeWorkpaperCertification } from '../../../queries/use-revoke-workpaper-certification';
import { useWorkpaperCertification } from '../../../queries/use-workpaper-certification';
import { Workpaper, WorkpaperCertification, WorkpaperRoleEnum } from '../../../types/workpaper';
import CertificationStep from './CertificationStep';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../../intercom-events';

const ROLE_ORDER = [WorkpaperRoleEnum.PREPARER, WorkpaperRoleEnum.REVIEWER, WorkpaperRoleEnum.APPROVER];

type CertificationModalProps = {
    workpaper: Workpaper;
    onClose: (completePrompt?: boolean) => void;
};

type CertificationContentProps = CertificationModalProps & {
    certification: WorkpaperCertification;
};

function CertificationModal({ workpaper, onClose }: CertificationModalProps): JSX.Element {
    const certificationQuery = useWorkpaperCertification(workpaper.id);

    return (
        <Modal onClose={onClose} isOpen={true}>
            {certificationQuery.isLoading && (
                <div className="flex items-center justify-center w-[400px] h-[400px]">
                    <Spinner />
                </div>
            )}
            {certificationQuery.data?.certification && (
                <CertificationContent certification={certificationQuery.data.certification} workpaper={workpaper} onClose={onClose} />
            )}
        </Modal>
    );
}
function CertificationContent({ certification, workpaper, onClose }: CertificationContentProps): JSX.Element {
    const certifyMutation = useCertifyWorkpaper();
    const revokeCertificationMutation = useRevokeWorkpaperCertification();
    const { trackEvent } = useIntercom();
    const { role, readOnly } = useWorkpaperContext();
    const { id } = useUserContext();

    function certify(toCertifyRole: WorkpaperRoleEnum): void {
        certifyMutation.mutate(
            { role: toCertifyRole },
            {
                onSuccess: () => {
                    trackEvent(intercomEvents.workpaper.certified.event, intercomEvents.workpaper.certified.metaData(toCertifyRole));
                    if (ROLE_ORDER.indexOf(role) <= ROLE_ORDER.indexOf(toCertifyRole)) {
                        onClose(toCertifyRole === WorkpaperRoleEnum.APPROVER);
                    }
                    toastNotification('success', 'Workpaper is certified!');
                },
                onError: (err) => {
                    toastNotification('error', 'Unable to certify workpaper!', err.response?.data.message);
                },
            }
        );
    }

    function revokeCertification(toRevokeRole: WorkpaperRoleEnum): void {
        revokeCertificationMutation.mutate(
            { role: toRevokeRole },
            {
                onSuccess: () => {
                    onClose();
                    toastNotification('success', 'Workpaper certification is revoked!');
                },
                onError: (err) => {
                    toastNotification('error', 'Unable to revoke workpaper certification!', err.response?.data.message);
                },
            }
        );
    }

    const { preparer, reviewer, approver } = certification;

    // Check if a user can certify for the specific role
    function getDisableCertifyHint(toCertifyRole: WorkpaperRoleEnum): string | undefined {
        if (readOnly) {
            return 'This workpaper is read-only mode.';
        }

        if (!role) {
            return 'You must have a valid workpaper role.';
        }

        if (!workpaper.assigned.map((a) => a.id).includes(id)) {
            return 'Only a workpaper assignee can certify the workpaper.';
        }

        // User with role : PREPARER / REVIEWER / APPROVER can certify on PREPARER items
        if (toCertifyRole === WorkpaperRoleEnum.PREPARER) {
            if (preparer?.tasks?.some((t) => !t.complete)) {
                return 'Certification criteria not met.';
            }

            if (ROLE_ORDER.indexOf(role) < ROLE_ORDER.indexOf(toCertifyRole)) {
                return 'This requires a preparer/reviewer/approver role.';
            }
        }

        // User with role : REVIEWER / APPROVER can certify on REVIEWER items
        if (toCertifyRole === WorkpaperRoleEnum.REVIEWER) {
            if (reviewer?.tasks?.some((t) => !t.complete)) {
                return 'Certification criteria not met.';
            }

            if (ROLE_ORDER.indexOf(role) < ROLE_ORDER.indexOf(toCertifyRole)) {
                return 'This requires a reviewer/approver role.';
            }
        }

        // User with role : APPROVER can certify on APPROVER items
        if (toCertifyRole === WorkpaperRoleEnum.APPROVER) {
            if (approver?.tasks?.some((t) => !t.complete)) {
                return 'Certification criteria not met.';
            }

            if (ROLE_ORDER.indexOf(role) < ROLE_ORDER.indexOf(toCertifyRole)) {
                return 'This requires an approver role.';
            }
        }

        return undefined;
    }

    // Check if a user can revoke workpaper certification for the specific role
    function canRevokeCertification(toRevokeRole: WorkpaperRoleEnum): boolean {
        if (readOnly) {
            return false;
        }

        const reviewerCertifiedBy = reviewer?.certifiedBy?.id;
        const approverCertifiedBy = approver?.certifiedBy?.id;

        if (toRevokeRole === WorkpaperRoleEnum.PREPARER) {
            // A preparer can only revoke the preparer items certification if the reviewer/approver items haven't been certified
            if (role === toRevokeRole && !reviewerCertifiedBy && !approverCertifiedBy) {
                return true;
            }

            // A reviewer can revoke the preparer items certification if approver items haven't been certified
            if (role === WorkpaperRoleEnum.REVIEWER && !approverCertifiedBy) {
                return true;
            }

            // An approver can revoke the preparer items certification
            if (role === WorkpaperRoleEnum.APPROVER) {
                return true;
            }
        }

        if (toRevokeRole === WorkpaperRoleEnum.REVIEWER) {
            // A reviewer can only revoke the reviewer items certification if the approver items haven't been certified
            if (role === toRevokeRole && !approverCertifiedBy) {
                return true;
            }

            // An approver can revoke the reviewer items certification
            if (role === WorkpaperRoleEnum.APPROVER) {
                return true;
            }
        }

        // An approver can revoke the approver items certification
        if (toRevokeRole === WorkpaperRoleEnum.APPROVER && role === WorkpaperRoleEnum.APPROVER) {
            return true;
        }

        return false;
    }

    return (
        <div className="flex items-start">
            <div className="p-8 flex flex-col">
                <CertifyImage className="h-24 w-24 mb-4 self-center" />
                <p className="text-gray-900 font-medium self-start mb-4">
                    Certify {new Date(workpaper.periodEnd).toLocaleDateString(navigator.language)} - {workpaper.name}
                </p>

                {/* Preparer Items */}
                {preparer && (
                    <CertificationStep
                        title="Preparer Items"
                        status={preparer.certifiedBy ? 'CERTIFIED' : preparer.tasks?.every((t) => t.complete) ? 'AWAITING' : 'NOT_READY'}
                        tasks={preparer.tasks}
                        certifiedAt={preparer.certifiedOn}
                        certifiedBy={preparer.certifiedBy}
                        canRevokeCertification={canRevokeCertification(WorkpaperRoleEnum.PREPARER)}
                        disableCertifyHint={getDisableCertifyHint(WorkpaperRoleEnum.PREPARER)}
                        onCertify={(): void => certify(WorkpaperRoleEnum.PREPARER)}
                        onRevokeCertification={(): void => revokeCertification(WorkpaperRoleEnum.PREPARER)}
                    />
                )}
                <div className="mt-4" />
                {/* Reviewer Items */}
                {reviewer && (
                    <CertificationStep
                        title="Reviewer Items"
                        status={reviewer.certifiedBy ? 'CERTIFIED' : reviewer.tasks?.every((t) => t.complete) ? 'AWAITING' : 'NOT_READY'}
                        tasks={reviewer.tasks}
                        certifiedAt={reviewer.certifiedOn}
                        certifiedBy={reviewer.certifiedBy}
                        canRevokeCertification={canRevokeCertification(WorkpaperRoleEnum.REVIEWER)}
                        disableCertifyHint={getDisableCertifyHint(WorkpaperRoleEnum.REVIEWER)}
                        onCertify={(): void => certify(WorkpaperRoleEnum.REVIEWER)}
                        onRevokeCertification={(): void => revokeCertification(WorkpaperRoleEnum.REVIEWER)}
                    />
                )}
                <div className="mt-4" />
                {/* Approver Items */}
                {approver && (
                    <CertificationStep
                        title="Approver Items"
                        status={approver.certifiedBy ? 'CERTIFIED' : approver.tasks?.every((t) => t.complete) ? 'AWAITING' : 'NOT_READY'}
                        tasks={approver.tasks}
                        certifiedAt={approver.certifiedOn}
                        certifiedBy={approver.certifiedBy}
                        canRevokeCertification={canRevokeCertification(WorkpaperRoleEnum.APPROVER)}
                        disableCertifyHint={getDisableCertifyHint(WorkpaperRoleEnum.APPROVER)}
                        onCertify={(): void => certify(WorkpaperRoleEnum.APPROVER)}
                        onRevokeCertification={(): void => revokeCertification(WorkpaperRoleEnum.APPROVER)}
                    />
                )}
            </div>
            <div className="pt-8 pr-4">
                <ButtonV2 type="hollow" size="lg" onClick={onClose} leftIcon={<XMarkIcon className="text-gray-500" />} />
            </div>
        </div>
    );
}

export default CertificationModal;
